import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiError } from '@sw/api-client';
import { reportError } from './errors/reporter';

/**
 * QueryClient con error handling global a 3 niveles:
 *
 * 1. **401 mid-session** → emite evento `auth:expired` (window-level) que el AuthProvider
 *    escucha para invalidar /me + navegar a /login preservando origen.
 *
 *    Diferencia vs legacy: el legacy usaba `router.navigate()` del createBrowserRouter
 *    imperativo. El monorepo usa BrowserRouter declarativo + Routes — no hay router global
 *    importable. Por lo tanto el evento NO incluye la navegación; el AuthProvider o un
 *    layout component escucha y dispara navigate() vía useNavigate desde React context.
 *
 * 2. **Reporter** → todos los errores se mandan al reporter (batch + dedup) para que el
 *    equipo vea qué falla en runtime.
 *
 * 3. **Retry policy** → 4xx no se reintenta (cliente, no transient). 5xx + network
 *    reintenta hasta 2 veces. Mutations NO reintentan por default — el caller decide.
 */
export function createAppQueryClient(): QueryClient {
  const queryCache = new QueryCache({
    onError: (error, query) => {
      handleAuthError(error);
      reportError(error, `query:${String(query.queryKey)}`);
    },
  });

  const mutationCache = new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      handleAuthError(error);
      reportError(error, `mutation:${mutation.options.mutationKey?.join(':') ?? 'anon'}`);
    },
  });

  return new QueryClient({
    queryCache,
    mutationCache,
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return failureCount < 2;
        },
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

function handleAuthError(error: unknown): void {
  if (!(error instanceof ApiError) || error.status !== 401) return;

  const currentPath = window.location.pathname;
  if (currentPath === '/login' || currentPath === '/auth/magic-link/verify') return;

  // Evento custom — un listener en el árbol React (puede ser AuthProvider o un layout)
  // se suscribe vía useEffect + window.addEventListener y dispara useNavigate.
  // El payload del CustomEvent incluye la ruta original para que el listener arme el
  // returnUrl. Anti-loop: si ya estamos en /login o verify, no se emite (early return arriba).
  window.dispatchEvent(
    new CustomEvent('auth:expired', {
      detail: { from: currentPath + window.location.search },
    }),
  );
}

declare global {
  interface WindowEventMap {
    'auth:expired': CustomEvent<{ from: string }>;
  }
}
